import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useState, useEffect } from "react";
import API from "../api/api01";
import { toast } from "sonner";
import {
  Calendar,
  Phone,
  Mail,
  MessageSquare,
  Clock,
  Search,
  Filter,
  PhoneCall,
  Video,
  Users,
  ExternalLink,
  User,
  MapPin,
  Tag,
  Activity
} from "lucide-react";
import { cn } from "../ui/utils";

interface ActivityItemData {
  id: string;
  lead_id?: string;
  type: string;
  activity_type: string;
  leadName: string;
  subject: string;
  description: string;
  date: string;
  time: string;
  status: string;
  counselor: {
    name: string;
    avatar: string;
  };
}

interface LeadHistory {
  lead: {
    id: string;
    name: string;
    email: string;
    phone: string;
    course: string;
    source: string;
    status: string;
    counselor: string;
  };
  history: Array<{
    time: string;
    text: string;
    type: string;
    status: string;
    notes?: string;
    recording?: string;
  }>;
}

export function LeadActivities() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activityFilter, setActivityFilter] = useState("all");

  const [activities, setActivities] = useState<ActivityItemData[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Popup States
  const [selectedLead, setSelectedLead] = useState<LeadHistory | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await API.get("/dashboard/activity");
      if (res.data) {
        const mappedActivities = res.data.map((a: any) => ({
          id: a.id,
          lead_id: a.lead_id,
          type: a.activity_type,
          activity_type: a.activity_type,
          leadName: a.lead_name || "Unknown",
          subject: a.text,
          description: a.text,
          date: new Date(a.time).toLocaleDateString(),
          time: new Date(a.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: a.status || "completed",
          counselor: {
            name: a.counselor || "System",
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${a.lead_id || a.id}`,
          },
        }));
        setActivities(mappedActivities);
        if (mappedActivities.length > 0) {
          toast.success("Activities loaded");
        } else {
          toast.error("No records found");
        }
      }
    } catch (err) {
      console.error("Failed to fetch activities", err);
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const handleActivityClick = async (leadId: string | undefined) => {
    if (!leadId) return;
    
    setHistoryLoading(true);
    setIsHistoryOpen(true);
    try {
      const response = await API.get(`/leads/${leadId}/history`);
      setSelectedLead(response.data);
    } catch (err) {
      console.error("Failed to fetch lead history", err);
      toast.error("Failed to load lead details");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Dynamic Stats
  const stats = {
    calls: activities.filter(a => a.type === 'call').length,
    emails: activities.filter(a => a.type === 'email' || a.type === 'registration').length,
    meetings: activities.filter(a => a.type === 'meeting').length,
    video: activities.filter(a => a.type === 'video').length
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "call": return <Phone className="h-5 w-5" />;
      case "email": return <Mail className="h-5 w-5" />;
      case "registration": return <Tag className="h-5 w-5" />;
      case "meeting": return <Users className="h-5 w-5" />;
      case "video": return <Video className="h-5 w-5" />;
      case "lms_capture": return <Clock className="h-5 w-5" />;
      default: return <Clock className="h-5 w-5" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "call": return "bg-blue-500/10 text-blue-500";
      case "email": return "bg-purple-500/10 text-purple-500";
      case "registration": return "bg-green-500/10 text-green-500";
      case "meeting": return "bg-orange-500/10 text-orange-500";
      case "lms_capture": return "bg-indigo-500/10 text-indigo-500";
      default: return "bg-gray-500/10 text-gray-500";
    }
  };

  const filteredActivities = activities.filter((activity) => {
    const matchesSearch =
      activity.leadName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activityFilter === "all" || activity.type === activityFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Lead Activities</h1>
        <p className="text-muted-foreground mt-1">
          Track all interactions and follow-ups with prospective students
        </p>
      </div>

      {/* Dynamic Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Calls", val: stats.calls, icon: Phone, color: "text-blue-500", bg: "bg-blue-500/10" },
          { label: "Emails", val: stats.emails, icon: Mail, color: "text-purple-500", bg: "bg-purple-500/10" },
          { label: "Meetings", val: stats.meetings, icon: Users, color: "text-green-500", bg: "bg-green-500/10" },
          { label: "Video Calls", val: stats.video, icon: Video, color: "text-orange-500", bg: "bg-orange-500/10" }
        ].map(s => (
          <Card key={s.label} className="shadow-sm border-none bg-slate-50 dark:bg-slate-900/40">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className={cn("p-3 rounded-lg", s.bg)}>
                  <s.icon className={cn("h-6 w-6", s.color)} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.val}</p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="shadow-sm border-none bg-slate-50 dark:bg-slate-900/40">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-white dark:bg-[#0a0a0a] border-none"
              />
            </div>
            <Select value={activityFilter} onValueChange={setActivityFilter}>
              <SelectTrigger className="w-full md:w-48 bg-white dark:bg-[#0a0a0a] border-none">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Activity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="call">Calls</SelectItem>
                <SelectItem value="email">Emails</SelectItem>
                <SelectItem value="registration">New Leads</SelectItem>
                <SelectItem value="meeting">Meetings</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activities Timeline */}
      <Card className="shadow-sm border-none">
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading activities...</p>
            ) : filteredActivities.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Clock className="h-12 w-12 mb-4 opacity-20" />
                <p className="text-lg font-medium">No records found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              filteredActivities.map((activity) => (
                <div
                  key={activity.id}
                  onClick={() => handleActivityClick(activity.lead_id)}
                  className="flex gap-4 p-4 rounded-xl border border-border/40 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-all cursor-pointer group"
                >
                  <div className={cn("p-3 rounded-lg h-fit", getActivityColor(activity.type))}>
                    {getActivityIcon(activity.type)}
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold group-hover:text-primary transition-colors">{activity.subject}</h4>
                        <p className="text-sm text-muted-foreground">Lead: {activity.leadName}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{activity.status}</Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{activity.date}</div>
                      <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{activity.time}</div>
                      <div className="flex items-center gap-1.5 ">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={activity.counselor.avatar} />
                          <AvatarFallback>{activity.counselor.name?.[0] || 'C'}</AvatarFallback>
                        </Avatar>
                        <span>{activity.counselor.name || 'Counselor'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* History Popup */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto border-none">
          <DialogHeader>
            <DialogTitle>Lead Details & History</DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="py-20 text-center text-muted-foreground">Loading details...</div>
          ) : selectedLead ? (
            <div className="space-y-6 py-4">
              {/* Profile Overview */}
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedLead?.lead?.id || 'unknown'}`} />
                  <AvatarFallback>{selectedLead?.lead?.name ? selectedLead.lead.name[0] : 'L'}</AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">{selectedLead?.lead?.name || 'Lead Details'}</h3>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {selectedLead?.lead?.email || 'No Email'}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {selectedLead?.lead?.phone || 'No Phone'}</span>
                  </div>
                </div>
              </div>

              {/* Attributes Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-xl border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Source</p>
                  <p className="font-medium flex items-center gap-2 text-sm"><ExternalLink className="h-3.5 w-3.5 text-blue-500" /> {selectedLead.lead?.source || 'N/A'}</p>
                </div>
                <div className="p-3 rounded-xl border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Applying For</p>
                  <p className="font-medium flex items-center gap-2 text-sm"><Briefcase className={cn("h-3.5 w-3.5 text-purple-500")} /> {selectedLead.lead?.course || 'N/A'}</p>
                </div>
                <div className="p-3 rounded-xl border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Status</p>
                  <Badge className="bg-blue-500 text-white hover:bg-blue-600">{selectedLead.lead?.status || 'New'}</Badge>
                </div>
                <div className="p-3 rounded-xl border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Assigned Counselor</p>
                  <p className="font-medium flex items-center gap-2 text-sm"><User className="h-3.5 w-3.5 text-orange-500" /> {selectedLead.lead?.counselor || 'System'}</p>
                </div>
              </div>

              {/* History Timeline */}
              <div className="space-y-4">
                <h4 className="font-bold flex items-center gap-2"><Activity className="h-4 w-4" /> Interaction History</h4>
                <div className="space-y-4 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[1px] before:bg-border/60">
                  {selectedLead?.history?.map((h, i) => (
                    <div key={i} className="flex gap-4 relative pl-8">
                      <div className={cn("absolute left-0 top-1 h-6 w-6 rounded-full border-4 border-background flex items-center justify-center", 
                        h.type === 'call' ? 'bg-blue-500' : h.type === 'registration' ? 'bg-green-500' : 'bg-slate-400')}>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{h.text}</p>
                        {h.notes && <p className="text-sm text-muted-foreground mt-1 bg-white dark:bg-slate-800/50 p-2 rounded-lg italic">"{h.notes}"</p>}
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                          <Clock className="h-2.5 w-2.5" /> {h.time ? new Date(h.time).toLocaleString() : 'Recent'}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!selectedLead?.history || selectedLead.history.length === 0) && (
                    <p className="text-sm text-muted-foreground pl-8 italic">No interaction history found for this lead.</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Briefcase({ className }: { className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  );
}
